import type { OneSignalV16API, OneSignalNotificationEvent } from '@/types/onesignal'
import { notificationService } from '@/services/notificationService'
import { useNotifications } from '@/composables/useNotifications'

declare global {
  interface Window {
    OneSignal: OneSignalV16API
    OneSignalDeferred: Array<(OneSignal: OneSignalV16API) => void>
  }
}

type InitializationState = 'idle' | 'initializing' | 'initialized' | 'failed'

type TokenResult = { id: string; type: 'OneSignal' | 'Subscription' }

class OneSignalService {
  private static instance: OneSignalService
  private initState: InitializationState = 'idle'
  private initPromise: Promise<void> | null = null
  private lastRegisteredUserId: string | null = null

  private constructor() { }

  static getInstance(): OneSignalService {
    if (!OneSignalService.instance) {
      OneSignalService.instance = new OneSignalService()
    }
    return OneSignalService.instance
  }

  /**
   * Initialize OneSignal SDK - ensures it's only initialized once
   */
  async initialize(): Promise<void> {
    // Return existing promise if already initializing
    if (this.initState === 'initializing' && this.initPromise) {
      return this.initPromise
    }

    // Return immediately if already initialized
    if (this.initState === 'initialized') {
      console.log('🔔 OneSignal already initialized')
      return
    }

    this.initState = 'initializing'
    this.initPromise = this._doInitialization()

    try {
      await this.initPromise
      this.initState = 'initialized'
      console.log('✅ OneSignal service initialized successfully')
    } catch (error) {
      this.initState = 'failed'
      console.error('❌ OneSignal service initialization failed:', error)
      throw error
    }
  }

  /**
   * Internal initialization logic
   */
  private _doInitialization(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Ensure OneSignalDeferred array exists
      window.OneSignalDeferred = window.OneSignalDeferred || []

      // Only add the initialization callback once
      window.OneSignalDeferred.push(async (OneSignal: OneSignalV16API) => {
        try {
          console.log('🔔 OneSignal SDK loaded, initializing...')

          const appId = import.meta.env.VITE_ONESIGNAL_APP_ID
          if (!appId) {
            throw new Error('VITE_ONESIGNAL_APP_ID is missing')
          }
          console.log('🔔 OneSignal App ID found:', appId.substring(0, 8) + '...')

          await OneSignal.init({
            appId: appId,
            allowLocalhostAsSecureOrigin: true,
            serviceWorkerPath: '/OneSignalSDKWorker.js',
            serviceWorkerParam: { scope: '/' },
            autoRegister: false,
            promptOptions: {
              slidedown: {
                prompts: [
                  {
                    type: 'push',
                    autoPrompt: false,
                    text: {
                      actionMessage: "We'd like to show you notifications for the latest news and updates.",
                      acceptButton: 'Allow',
                      cancelButton: 'Cancel',
                    },
                  },
                ],
              },
            },
          })

          // Add listener for foreground notifications
          if (OneSignal.Notifications && typeof OneSignal.Notifications.addEventListener === 'function') {
            OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: OneSignalNotificationEvent) => {
              console.log('🔔 OneSignal foreground notification:', event)
              try {
                const { handleOneSignalNotification } = useNotifications()
                // Pass the inner notification object
                handleOneSignalNotification(event.notification as any)
              } catch (err) {
                console.error('Error handling OneSignal notification:', err)
              }
            })

            // Optional: Listen for clicks
             OneSignal.Notifications.addEventListener('click', (event: any) => {
               console.log('🔔 OneSignal notification clicked:', event)
             })
          }

          console.log('✅ OneSignal SDK initialized')
          resolve()
        } catch (error) {
          console.error('❌ OneSignal SDK initialization failed:', error)
          reject(error)
        }
      })
    })
  }

  /**
   * Register device token for authenticated user
   */
  async registerDeviceToken(userId: string): Promise<void> {
    // Check if we already registered for this user (in-memory check)
    if (this.lastRegisteredUserId === userId) {
      console.log('🔔 OneSignal already registered for user:', userId)
      return
    }

    // Ensure OneSignal is initialized
    await this.initialize()

    // Wait for OneSignal to be available
    await this._waitForOneSignal()

    const OneSignal = window.OneSignal

    try {
      console.log('🔔 Starting OneSignal registration for user:', userId)

      // Show slidedown prompt for push notifications
      console.log('🔔 Showing push notification prompt...')
      await OneSignal.Slidedown.promptPush()

      const currentExternalId = OneSignal.User.externalId

      // Check if we are already logged in as this user
      if (currentExternalId === userId) {
        console.log('✅ OneSignal already logged in with ID:', userId)
      } else {
        // If we are logged in as someone else, logout first
        if (currentExternalId) {
          console.log('🔔 OneSignal currently logged in as:', currentExternalId, 'Logging out...')
          await OneSignal.logout()
          console.log('✅ OneSignal logged out to ensure clean state')
        }

        // Set external user ID (Identity)
        console.log('🔔 Setting external user ID (Identity):', userId)
        await OneSignal.login(userId)
        console.log('✅ OneSignal user logged in with ID:', userId)
      }

      // Get the OneSignal user ID
      // We prioritize Identity ID, but fallback to Subscription ID on timeout to ensure request is sent.
      console.log('🔔 Getting OneSignal user ID...')
      let result = await this._waitForOneSignalUserId(OneSignal)

      // Recovery attempt if ID retrieval failed
      if (!result) {
        console.warn('⚠️ OneSignal ID retrieval failed, attempting recovery (logout -> login)...')
        await OneSignal.logout()
        await new Promise(resolve => setTimeout(resolve, 1000))
        await OneSignal.login(userId)
        result = await this._waitForOneSignalUserId(OneSignal)
      }

      if (!result) {
        throw new Error('Could not obtain OneSignal user ID (Identity or Subscription)')
      }

      const { id: onesignalId, type: tokenType } = result
      console.log(`✅ OneSignal user ID obtained: ${onesignalId} (Type: ${tokenType})`)

      // Final validation
      const finalExternalId = OneSignal.User.externalId || null
      if (finalExternalId !== userId) {
        console.log('⚠️ External ID validation failed (Login might be pending):', {
          expected: userId,
          actual: finalExternalId,
        })
        // We continue anyway if we have an ID, to ensure registration
      }

      // Register with backend
      const deviceInfo = notificationService.getDeviceInfo()
      const tokenData = {
        token_id: onesignalId,
        token_type: tokenType,
        ...deviceInfo,
      }

      console.log('🔔 Registering device token with backend:', {
        ...tokenData,
        externalId: finalExternalId,
        note: `Sending ${tokenType} ID`
      })

      await notificationService.registerDeviceToken(tokenData)

      // Mark as registered for this user
      this.lastRegisteredUserId = userId

      console.log('✅ Device token registered successfully!', {
        deviceId: onesignalId,
        tokenType,
        externalId: finalExternalId,
        userId,
      })
    } catch (error) {
      console.error('❌ OneSignal registration failed:', error)
      throw error
    }
  }

  /**
   * Clear registration state (call on logout)
   */
  async clearRegistration(): Promise<void> {
    this.lastRegisteredUserId = null

    // Clear OneSignal external user ID
    if (window.OneSignal && this.initState === 'initialized') {
      try {
        await window.OneSignal.logout()
        console.log('✅ OneSignal user logged out successfully')
      } catch (error) {
        console.log('⚠️ OneSignal logout failed:', error)
      }
    }
  }

  /**
   * Get current initialization state
   */
  getInitializationState(): InitializationState {
    return this.initState
  }

  /**
   * Check if already registered for a specific user
   */
  isRegisteredForUser(userId: string): boolean {
    return this.lastRegisteredUserId === userId
  }

  /**
   * Wait for OneSignal to be available
   */
  private async _waitForOneSignal(): Promise<void> {
    if (window.OneSignal) return

    console.log('⏳ Waiting for OneSignal to be available...')
    return new Promise((resolve) => {
      const checkOneSignal = () => {
        if (window.OneSignal) {
          resolve()
        } else {
          setTimeout(checkOneSignal, 100)
        }
      }
      checkOneSignal()
    })
  }

  /**
   * Wait for OneSignal user ID to become available
   * Prioritizes Identity ID, falls back to Subscription ID on timeout
   */
  private async _waitForOneSignalUserId(
    OneSignal: OneSignalV16API,
    timeoutMs = 15000,
  ): Promise<TokenResult | null> {
    const startTime = Date.now()

    const getIdentityId = (): string | null => {
      if (OneSignal.User && OneSignal.User.onesignalId) {
        return OneSignal.User.onesignalId
      }
      return null
    }

    const getSubscriptionId = (): string | null => {
      if (OneSignal.User && OneSignal.User.PushSubscription && OneSignal.User.PushSubscription.id) {
        return OneSignal.User.PushSubscription.id
      }
      return null
    }

    // Check immediately for Identity ID
    const identityId = getIdentityId()
    if (identityId) {
      console.log('🔔 OneSignal Identity ID immediately available:', identityId)
      return { id: identityId, type: 'OneSignal' }
    }

    // Debug current state
    console.log('🔔 Current OneSignal User State:', {
       onesignalId: OneSignal.User?.onesignalId,
       externalId: OneSignal.User?.externalId,
       pushSubscriptionId: OneSignal.User?.PushSubscription?.id
    })

    // Polling mechanism
    return new Promise((resolve) => {
      let resolved = false

      const cleanup = () => {
         if (OneSignal.User && typeof OneSignal.User.removeEventListener === 'function') {
           try {
             OneSignal.User.removeEventListener('change', onUserChange)
           } catch(e) { /* ignore */ }
         }
      }

      const onUserChange = (event: any) => {
        if (resolved) return
        console.log('🔔 OneSignal user change event:', event)
        const id = getIdentityId()
        if (id) {
          console.log('🔔 OneSignal Identity ID resolved via change event:', id)
          resolved = true
          cleanup()
          resolve({ id, type: 'OneSignal' })
        }
      }

      // Add listener safely
      if (OneSignal.User && typeof OneSignal.User.addEventListener === 'function') {
        try {
          OneSignal.User.addEventListener('change', onUserChange)
        } catch (e) {
          console.warn('OneSignal addEventListener failed:', e)
        }
      }

      const intervalId = setInterval(() => {
        if (resolved) {
          clearInterval(intervalId)
          return
        }

        const id = getIdentityId()
        if (id) {
          console.log('🔔 OneSignal Identity ID resolved via polling:', id)
          resolved = true
          clearInterval(intervalId)
          cleanup()
          resolve({ id, type: 'OneSignal' })
        } else if (Date.now() - startTime > timeoutMs) {
          console.warn('⚠️ OneSignal Identity ID timeout reached')

          // Fallback to Subscription ID
          const subId = getSubscriptionId()
          if (subId) {
             console.warn('⚠️ Falling back to Subscription ID (Identity ID missing). This may cause targeting issues.', subId)
             resolved = true
             clearInterval(intervalId)
             cleanup()
             resolve({ id: subId, type: 'Subscription' })
          } else {
             console.error('❌ Failed to get both Identity ID and Subscription ID')
             resolved = true
             clearInterval(intervalId)
             cleanup()
             resolve(null)
          }
        }
      }, 500)
    })
  }
}

// Export singleton instance
export const oneSignalService = OneSignalService.getInstance()
